/**
 * Saving an AMS incident's read flag to the server.
 *
 * read_at is shared triage state (it orders everyone's inbox), so the API
 * accepts it only from roles that may change the incident ("execute").
 * A Viewer or Commentator is refused (403), and an incident that is gone or
 * out of reach answers 404: for them the item stays read in this browser
 * only, which is the intended outcome, not a failure. Anything else — a
 * network error, a 5xx, an expired session (401) — is a real failure that
 * the caller reports and can retry.
 */
import { ApiError } from '../lib/apiError';

export type IncidentReadOutcome = 'saved' | 'local-only';

function statusOf(error: unknown): number | undefined {
  if (error instanceof ApiError) return error.status;
  if (error && typeof error === 'object' && 'status' in error) {
    const status = Number((error as { status?: unknown }).status);
    return Number.isFinite(status) ? status : undefined;
  }
  return undefined;
}

/** Sends the read flag; resolves 'local-only' when the caller may not share it, rejects on real failures. */
export async function persistIncidentRead(send: () => Promise<unknown>): Promise<IncidentReadOutcome> {
  try {
    await send();
    return 'saved';
  } catch (error) {
    const status = statusOf(error);
    if (status === 403 || status === 404) return 'local-only';
    throw error;
  }
}

/**
 * persistIncidentRead for fire-and-forget callers: never leaves a rejected
 * promise unhandled. A real failure is logged and handed to onFailure, so the
 * caller can undo its local read mark and the user can try again.
 */
export function syncIncidentRead(
  incidentId: string,
  send: () => Promise<unknown>,
  onFailure: (error: unknown) => void,
): Promise<IncidentReadOutcome | 'failed'> {
  return persistIncidentRead(send).catch((error: unknown) => {
    console.warn(`Could not save the read state of incident ${incidentId}`, error);
    onFailure(error);
    return 'failed' as const;
  });
}
